import type { RawPayment } from "@lemmonhq/types";
import { useLocalization } from "@lemmonhq/uikit";
import { api } from "@app/data/api";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { CreditNoteSubmit } from "./types";

/**
 * Issues a credit note (items, whole sale, or a free amount) for a sale from
 * CreditNoteModal, in the active shift when there is one, and toasts the
 * outcome. Resolves to the result, or undefined when it failed.
 */
export function useCreditNoteSubmit() {
  const trpc = useTrpc();
  const { showToast } = useToast();
  const { t } = useLocalization();

  return async (payment: RawPayment, input: CreditNoteSubmit) => {
    try {
      const activeShift = await api.getActiveShift().catch(() => undefined);
      const res = await trpc.payments.createCreditNote.mutate({
        payment_id: payment.id,
        items: input.items,
        amount: input.amount,
        payment_method: input.payment_method,
        reopen_table: input.reopen_table,
        shift_id: activeShift?.id,
      });
      showToast(
        res.result.reopened
          ? t("reports.tx.credit_note_items.success_reopen")
          : t("reports.tx.page.refund_created"),
        { type: "success" },
      );
      return res.result;
    } catch (error) {
      console.error("Failed to create credit note", error);
      showToast(
        (error as Error)?.message || t("reports.tx.credit_note_items.failed"),
        { type: "error" },
      );
      return undefined;
    }
  };
}
